import { SortingState } from '@tanstack/react-table'
import { FilterWorkersWithVisibleItems } from 'components/buttons/FilterWorkersWithVisibleItemsButtonGroup'
import { useLocalStorageState } from 'hooks/use-local-storage-state'
import { createSetOperationsFor } from '@utils/createSetOperations'
import { createStringOperationsFor } from '@utils/createStringOperations'
import { createSortingOperationsFor } from '@utils/createSortingOperations'
import { FilterAlertType } from '@features/admin/features/submissions-admin/types'
import { Anomalies } from 'components/buttons/AnomalousTimeCardButtons'
import { useMemo } from 'react'

interface AdminLocalStorageState {
  hiddenDays: Set<number>
  hiddenStatuses: Set<Status>
  facilityIds: Set<number>
  approvalGroupIds: Set<number>
  filteredAlertTypes: Set<FilterAlertType>
  filteredAnomalyTypes: Set<Anomalies>
  filterEmployeesWithVisibleTimeCards: FilterWorkersWithVisibleItems
  sorting: SortingState
  employeeNameFilter: string
}

const defaultValues: AdminLocalStorageState = {
  hiddenDays: new Set(),
  hiddenStatuses: new Set(),
  facilityIds: new Set(),
  approvalGroupIds: new Set(),
  filterEmployeesWithVisibleTimeCards: 'all',
  sorting: [
    {
      id: 'employeeName',
      desc: false,
    },
  ],
  employeeNameFilter: '',
  filteredAlertTypes: new Set(),
  filteredAnomalyTypes: new Set(),
}

export const useSubmissionsAdminLocalStorage = () => {
  const { state, setState, debounced, clear, changedAttributes, isChanged } =
    useLocalStorageState('timeLoggerSubmissionAdminState', defaultValues)

  return useMemo(
    () => ({
      hiddenDays: createSetOperationsFor('hiddenDays')(state, setState),
      hiddenStatuses: createSetOperationsFor('hiddenStatuses')(state, setState),
      facilityIds: {
        ...createSetOperationsFor('facilityIds')(state, setState),
        debounced: debounced?.facilityIds ?? new Set(),
      },
      approvalGroupIds: createSetOperationsFor('approvalGroupIds')(
        state,
        setState,
      ),
      filterEmployeesWithVisibleTimeCards: {
        value: state.filterEmployeesWithVisibleTimeCards,
        debounced: debounced?.filterEmployeesWithVisibleTimeCards ?? '',
        set: (value: FilterWorkersWithVisibleItems) => {
          setState((prev) => ({
            ...prev,
            filterEmployeesWithVisibleTimeCards: value,
          }))
        },
      },
      sorting: createSortingOperationsFor('sorting')(state, setState),
      employeeNameFilter: {
        ...createStringOperationsFor('employeeNameFilter')(state, setState),
        debounced: debounced?.employeeNameFilter ?? '',
      },
      filteredAlertTypes: createSetOperationsFor('filteredAlertTypes')(
        state,
        setState,
      ),
      filteredAnomalyTypes: createSetOperationsFor('filteredAnomalyTypes')(
        state,
        setState,
      ),
      clear,
      changedAttributes,
      isChanged,
    }),
    [state, setState, debounced, clear, changedAttributes, isChanged],
  )
}
